/**
 * HelpOverlay — 320px slide-in-from-right help panel (Day 60c).
 *
 * Mirrors NotificationPanel's layout shape (TopBar.tsx:104-197): fixed
 * top-[52px] right-0 w-80, flex-col with a header / tab-strip / body /
 * footer. Differs from NotificationPanel in that it hosts a 4-tab strip
 * (Docs / Shortcuts / Legend / Feedback) and persists open across navigation
 * via HelpOverlayContext (vs. NotificationPanel's local-state close-on-
 * link-click behavior).
 *
 * Esc handler shape lifted from ConfirmDialog.tsx:39-46. Focus return on
 * close happens in HelpOverlayContext.close() via requestAnimationFrame
 * → helpButtonRef.current?.focus().
 *
 * Docs tab (Day 100b.2): the overlay graduated to the four-tab shape with Docs
 * first + default-active (COMPONENT_STANDARDS §4). HelpDocsTab renders the
 * resolved slug's published article via the shared MarkdownView (body only — no
 * side rail; the 320px overlay is too narrow) over the useDocsPages() corpus.
 * activeTab + activeSlug live in HelpOverlayContext so open({ tab, slug })
 * deep-links from the contextual "?" / search / Related links.
 */
import { useEffect, type JSX } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import { useHelpOverlay } from '../../hooks/useHelpOverlay';
import { type HelpTab } from '../../contexts/HelpOverlayContext';
import HelpDocsTab from './HelpDocsTab';
import HelpShortcutsTab from './HelpShortcutsTab';
import HelpLegendTab from './HelpLegendTab';
import HelpFeedbackTab from './HelpFeedbackTab';

const TABS: ReadonlyArray<{ key: HelpTab; label: string }> = [
  { key: 'docs', label: 'Docs' },
  { key: 'shortcuts', label: 'Shortcuts' },
  { key: 'legend', label: 'Legend' },
  { key: 'feedback', label: 'Feedback' },
] as const;

// Vite-injected at build time via vite.config.ts:29 (Day 52.5). Local dev
// builds get the literal string 'dev'; Vercel builds get the commit SHA.
const BUILD_SHA: string = (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? 'dev') as string;
const BUILD_LABEL: string = BUILD_SHA === 'dev' ? 'Build dev' : `Build ${BUILD_SHA.slice(0, 7)}`;

export default function HelpOverlay(): JSX.Element | null {
  const { isOpen, close, activeTab, setActiveTab, activeSlug, setActiveSlug } = useHelpOverlay();

  // Esc dismiss — ConfirmDialog.tsx:39-46 pattern. Listener registered only
  // while open; cleanup on close + unmount.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-label="Help"
      className="fixed top-[52px] right-0 w-80 h-[calc(100vh-52px-36px)] bg-surface-1 border-l border-border shadow-lg flex flex-col"
      style={{
        zIndex: 'var(--z-dropdown, 100)',
        animation: 'slideInFromRight 200ms ease-in-out',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <span className="text-sm font-semibold text-ink-bright">Help</span>
        <button
          onClick={close}
          className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3 transition-colors cursor-pointer"
          aria-label="Close help"
        >
          <X size={14} />
        </button>
      </div>

      {/* Tab strip */}
      <div
        className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border flex-shrink-0"
        role="tablist"
        aria-label="Help sections"
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
            className={clsx(
              'flex-1 px-2 py-1 rounded text-2xs font-medium transition-colors cursor-pointer',
              activeTab === tab.key
                ? 'bg-accent/15 text-accent'
                : 'text-ink-muted hover:text-ink-secondary'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Body — per-tab content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'docs' && <HelpDocsTab slug={activeSlug} onSelectSlug={setActiveSlug} />}
        {activeTab === 'shortcuts' && <HelpShortcutsTab />}
        {activeTab === 'legend' && <HelpLegendTab />}
        {activeTab === 'feedback' && <HelpFeedbackTab />}
      </div>

      {/* Footer — Build SHA. "Last updated date" deferred to Day 65 with the
          release-notes table per COMPONENT_STANDARDS §4 (revised Day 60c). */}
      <div className="px-4 py-2 border-t border-border flex-shrink-0">
        <span className="text-2xs text-ink-muted font-mono">{BUILD_LABEL}</span>
      </div>
    </div>
  );
}
